// @flow
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';
import type { BookingPayment } from '../types';

export const getBookingPayments = createAsyncThunk(
  'booking/getBookingPayments',
  async (bookingId, thunkApi) => {
    try {
      const response = await thunkApi.extra.bookings.getBookingPayments(
        bookingId
      );

      return response.data;
    } catch (e) {
      return thunkApi.rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

const bookingPaymentsAdapter = createEntityAdapter<BookingPayment>();

export type State = {
  isLoading: boolean,
  hasLoaded: boolean
};

export const customInitialState: State = {
  isLoading: false,
  hasLoaded: false
};

export const initialState = bookingPaymentsAdapter.getInitialState(
  customInitialState
);

const bookingPaymentsSlice = createSlice({
  name: 'bookingPayments',
  initialState,
  reducers: {},
  extraReducers: {
    [getBookingPayments.pending]: (state: State): State =>
      bookingPaymentsAdapter.removeAll({
        ...state,
        isLoading: true,
        hasLoaded: false
      }),
    [getBookingPayments.fulfilled]: (
      state: State,
      action: PayloadAction
    ): State =>
      bookingPaymentsAdapter.setAll(
        {
          ...state,
          isLoading: false,
          hasLoaded: true
        },
        action.payload
      ),
    [getBookingPayments.rejected]: (state: State): State => ({
      ...state,
      isLoading: false,
      hasLoaded: true
    })
  }
});

const bookingPaymentsSelectors = bookingPaymentsAdapter.getSelectors(
  state => state[bookingPaymentsSlice.name]
);

export const {
  selectAll: selectBookingPaymentsAll,
  selectTotal: selectBookingPaymentsTotal
} = bookingPaymentsSelectors;

export const selectBookingPaymentsIsLoading = state =>
  state[bookingPaymentsSlice.name].isLoading ||
  state[bookingPaymentsSlice.name].hasLoaded === false;

export const selectBookingPaymentsHasNoResults = state =>
  state[bookingPaymentsSlice.name].hasLoaded === true &&
  selectBookingPaymentsTotal(state) === 0;

export const selectBookingPaymentPrimaryPaymentMethod = state => {
  const ids = bookingPaymentsSelectors.selectIds(state);

  if (ids.length === 0) {
    return null;
  }

  const firstId = ids[0];
  const payment = bookingPaymentsSelectors.selectById(state, firstId);

  if (!payment) {
    return null;
  }

  return payment.paymentSource;
};

export default bookingPaymentsSlice;
